import { DateTime } from "luxon";
import { AlertType } from "../../enums/alertTypes.enum";
import { AlertSeverity } from "../../enums/alertSeverities.enum";
import { AssetRef, AtedBy, Timestamp } from "../shared/refs";

export interface ThiefProtectionConfig {
  id?: string;
  isEnabled: boolean;
  alarmBufferMinutes: number;
  pushNotificationEnabled: boolean;
  escalationIntervalMinutes: number;
  contacts: Contact[];
  monday?: Day;
  tuesday?: Day;
  wednesday?: Day;
  thursday?: Day;
  friday?: Day;
  saturday?: Day;
  sunday?: Day;
  timezone: string;
}

/**
 * Creates a default thief protection configuration
 * @returns A default thief protection configuration object
 */
export function createDefaultThiefProtectionConfig(): ThiefProtectionConfig {
  return {
    isEnabled: false,
    alarmBufferMinutes: 0,
    escalationIntervalMinutes: 0,
    pushNotificationEnabled: false,
    contacts: [],
    monday: { openTimeWindows: [] },
    tuesday: { openTimeWindows: [] },
    wednesday: { openTimeWindows: [] },
    thursday: { openTimeWindows: [] },
    friday: { openTimeWindows: [] },
    saturday: { openTimeWindows: [] },
    sunday: { openTimeWindows: [] },
    timezone: DateTime.now().zoneName ?? "Europe/Berlin",
  };
}

/**
 * Maps thief protection config dto to thief protection config
 * @param dto The dto that is to be mapped
 * @returns The mapped thief protection config
 */
export function mapDtoToConfig(dto: ThiefProtectionConfigDTO): ThiefProtectionConfig {
  return {
    id: dto.id,
    isEnabled: dto.isActive,
    alarmBufferMinutes: dto.alarmBufferMinutes,
    pushNotificationEnabled: dto.pushNotificationEnabled,
    escalationIntervalMinutes: dto.escalationIntervalMinutes,
    contacts: dto.contacts.map(mapContactDtoToContact),
    monday: dto.monday,
    tuesday: dto.tuesday,
    wednesday: dto.wednesday,
    thursday: dto.thursday,
    friday: dto.friday,
    saturday: dto.saturday,
    sunday: dto.sunday,
    timezone: dto.timezone,
  };
}

export interface ThiefProtectionConfigDTO {
  id: string;
  building: { id: string; label: string; assetType: string };
  tenant: { id: string; label: string; assetType: string };
  location: { id: string; label: string; assetType: string };
  isActive: boolean;
  alarmBufferMinutes: number;
  pushNotificationEnabled: boolean;
  escalationIntervalMinutes: number;
  contacts: ContactDTO[];
  monday?: Day;
  tuesday?: Day;
  wednesday?: Day;
  thursday?: Day;
  friday?: Day;
  saturday?: Day;
  sunday?: Day;
  timezone: string;
  createdBy: { id: string; name: string };
  updatedBy?: { id: string; name: string };
  timestamp: { created_at: number; updated_at: number };
}

export type ThiefProtectionConfigUpdate = Omit<
  ThiefProtectionConfigDTO,
  "building" | "location" | "tenant" | "createdBy" | "updatedBy" | "timestamp"
>;
export type ThiefProtectionConfigCreate = Omit<ThiefProtectionConfigUpdate, "id">;

export interface Contact {
  firstName: string;
  lastName: string;
  type: "PRIMARY" | "ADDITIONAL";
  email: string;
  level: number;
  lang: "de" | "en";
}

/**
 * Maps contact dto to contact
 * @param dto The dto that is to be mapped
 * @returns The mapped contact
 */
export function mapContactDtoToContact(dto: ContactDTO): Contact {
  return {
    firstName: dto.firstName,
    lastName: dto.lastName,
    type: dto.contactType,
    email: dto.contactValue,
    level: dto.level,
    lang: dto.lang,
  };
}

/**
 * Maps contact to contact dto
 * @param contact The contact that is to be mapped
 * @returns The mapped dto
 */
export function mapContactToDto(contact: Contact): ContactDTO {
  return {
    firstName: contact.firstName,
    lastName: contact.lastName,
    contactType: contact.type,
    contactValue: contact.email,
    level: contact.level,
    lang: contact.lang,
  };
}

/**
 * Creates a default contact
 * @returns A default contact object
 */
export function createDefaultContact(): Contact {
  return {
    firstName: "",
    lastName: "",
    email: "",
    type: "PRIMARY",
    level: 1,
    lang: "de",
  };
}

export interface ContactDTO {
  firstName: string;
  lastName: string;
  contactType: "PRIMARY" | "ADDITIONAL";
  contactValue: string;
  level: number;
  lang: "de" | "en";
}

export interface Day {
  openTimeWindows?: OpenTimeWindow[];
}

export interface OpenTimeWindow {
  startTime?: string;
  endTime?: string;
}

export interface ThiefProtectionNotification {
  id: string;
  alertId: string;
  contactValue: string;
  level: number;
  sentAt: string;
  timestamp: Timestamp;
}

export interface ThiefProtectionAlert {
  id: string;
  gateSystemId: string;
  coreId: string;
  alertType: AlertType;
  severity: AlertSeverity;
  message: string;
  gatePosition: number;
  triggeredAt: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: AtedBy;
  notificationSent: boolean;
  escalationCount: number;
  lastNotificationLevel?: number;
  lastNotificationSentAt?: string;
  metadata: any;
  resolutionNote?: string;
  notifications?: ThiefProtectionNotification[];
  gateSystem: AssetRef;
}
